'use client';

import React from 'react';
import dayjs from 'dayjs';
import Link from 'next/link';
import { TbArrowUpRight } from 'react-icons/tb';
import { ContentManager, type ScreenerListItem } from '@benzinga/content-manager';
import { Button } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';

const toRelativePath = (link: string, postName: string) => {
  const path = link?.replace(/^https:\/\/www\.benzinga\.com\//, '') ?? '';
  return path && !path.startsWith('http') ? `/${path.replace(/\/$/, '')}` : `/screener/${postName}`;
};

export const ScreenerCollection = ({
  pageSize,
  screeners: initialScreeners,
}: {
  pageSize: number;
  screeners: ScreenerListItem[];
}) => {
  const [screeners, setScreeners] = React.useState(initialScreeners);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [noMoreResults, setNoMoreResults] = React.useState(initialScreeners.length < pageSize);
  const session = React.useContext(SessionContext);

  const handleFetchMore = async () => {
    if (loading) return;
    try {
      setLoading(true);
      setError(false);
      const res = await session
        .getManager(ContentManager)
        .getScreenerPosts({ limit: pageSize, offset: screeners.length });
      if (!Array.isArray(res?.ok)) {
        setError(true);
        return;
      }
      setScreeners(prev => {
        const shown = new Set(prev.map(screener => screener.id));
        return [...prev, ...res.ok.filter(screener => !shown.has(screener.id))];
      });
      if (res.ok.length < pageSize) setNoMoreResults(true);
    } finally {
      setLoading(false);
    }
  };

  if (!screeners.length) return null;

  return (
    <section className="mt-8 md:mt-10">
      <h2 className="m-0 text-lg font-bold leading-snug text-bzgray-800 md:text-xl">More Stock Screeners</h2>
      <p className="m-0 mb-4 mt-1 text-sm leading-relaxed text-bzgray-600">
        Curated screens built by Benzinga analysts, updated as the market moves.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4">
        {screeners.map(screener => (
          <Link
            key={screener.id}
            href={toRelativePath(screener.link, screener.post_name)}
            className="group flex flex-col gap-4 rounded-lg border border-bzblue-300 bg-white p-4 no-underline transition-colors hover:border-[#3f83f8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3f83f8] md:p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="m-0 text-balance text-[15px] font-bold leading-snug text-bzgray-800 transition-colors group-hover:text-[#3f83f8]">
                  {screener.post_title}
                </h3>
                {screener.description ? (
                  <p className="m-0 mt-1.5 line-clamp-2 text-pretty text-sm leading-relaxed text-bzgray-600">
                    {screener.description}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="mt-auto flex items-center justify-between gap-3 border-t border-bzgray-200 pt-3">
              <p className="m-0 text-xs text-bzgray-500">Updated {dayjs(screener.post_date).format('MMM D, YYYY')}</p>
              <span className="inline-flex items-center gap-0.5 text-xs font-bold text-[#3f83f8]">
                View screener
                <TbArrowUpRight
                  className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  aria-hidden
                />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {!noMoreResults && (
        <div className="mt-4 flex justify-center">
          <Button isLoading={loading} onClick={handleFetchMore} variant="fusion-outline">
            Load more
          </Button>
        </div>
      )}
      {error && (
        <p className="m-0 mt-2 text-center text-sm text-red-600">
          Couldn&apos;t load more screeners. Please try again.
        </p>
      )}
    </section>
  );
};

export default ScreenerCollection;
